import { Box, InputAdornment, MenuItem, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { Dispatch, SetStateAction, useContext } from "react";
import { OrganizationProfile } from "schemas/dashboard";
import { CustomEvent, Map } from "schemas/functions";

import StyledSelect from "components/Styled/Select";
import StyledTextField from "components/Styled/TextField";
import AboutFormLinks from "features/Auth/Onboarding/AboutForm/Links";
import { SocialMediaLinks } from "features/Auth/Onboarding/AboutForm/helpers";
import { isValidHttpUrl } from "utils/string";

import { ProfileFieldOption } from "../schema";
import styles from "../styles";

interface Props<FieldKey extends string> {
  items: FieldKey[];
  options: ProfileFieldOption<FieldKey>[];
  setItems: (next: FieldKey[]) => void;
  error: Map;
  setError: Dispatch<SetStateAction<Map>>;
  socialLinks: SocialMediaLinks[];
  setSocialLinks: Dispatch<SetStateAction<SocialMediaLinks[]>>;
  socialLinksValidation: { [key in string]: string | undefined };
  setSocialLinksValidation: Dispatch<
    SetStateAction<{ [key in string]: string | undefined }>
  >;
}

// Renders one section of profile-backed dropdown cards. Both
// "Tell us about yourself" and "Show your best work" use this; they only
// differ in the option list they're given and the items array they bind to.
export function ProfileFieldsList<FieldKey extends string>({
  items,
  options,
  setItems,
  error,
  setError,
  socialLinks,
  setSocialLinks,
  socialLinksValidation,
  setSocialLinksValidation,
}: Props<FieldKey>) {
  const { profile, setProfile } = useContext(OrganizationUserContext);

  return (
    <Box sx={styles.checklistContainer}>
      {items.map((fieldKey, idx) => {
        const option = options.find((o) => o.id === fieldKey) || options[0];
        const isPortfolio = fieldKey === "portfolioUrl";
        const portfolioErrored =
          isPortfolio &&
          error.field === "portfolioUrl" &&
          error.errorMessage.length > 0;
        const usedTypesByOthers = new Set(items.filter((_, i) => i !== idx));
        const profileValue =
          fieldKey === "socialMediaPage"
            ? ""
            : (profile as unknown as Record<string, string | undefined>)?.[
                fieldKey
              ] ?? "";
        return (
          <Box key={`${fieldKey}-${idx}`} sx={styles.bestWorkItem}>
            <Box sx={styles.bestWorkSelectCard}>
              <StyledSelect
                value={fieldKey}
                variant="standard"
                disableUnderline
                styles={styles.bestWorkSelect}
                renderValue={(selected: FieldKey) =>
                  options.find((o) => o.id === selected)?.label ?? ""
                }
                onChange={(e: CustomEvent) => {
                  const next = [...items];
                  next[idx] = e.target.value as FieldKey;
                  setItems(next);
                }}
              >
                {options.map((o) => (
                  <MenuItem
                    key={o.id}
                    value={o.id}
                    disabled={usedTypesByOthers.has(o.id)}
                  >
                    <Box sx={styles.bestWorkOption}>
                      <Typography sx={styles.bestWorkOptionLabel}>
                        {o.label}
                      </Typography>
                      <Typography sx={styles.bestWorkOptionSub}>
                        {o.sub}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </StyledSelect>
              {option.recommended && (
                <Box sx={styles.bestWorkRecommendedBadge}>Recommended</Box>
              )}
            </Box>
            <Box>
              {fieldKey === "socialMediaPage" ? (
                <AboutFormLinks
                  onboardingForm={(profile ?? {}) as Map}
                  setOnboardingForm={(next) => {
                    setProfile((prev) => {
                      const prevMap = (prev ?? {}) as Map;
                      const updated =
                        typeof next === "function" ? next(prevMap) : next;
                      return {
                        ...(prev ?? {}),
                        ...updated,
                      } as OrganizationProfile;
                    });
                  }}
                  setLinksValidation={setSocialLinksValidation}
                  links={socialLinks}
                  setLinks={setSocialLinks}
                  validation={socialLinksValidation}
                />
              ) : (
                (() => {
                  const isUrlField =
                    fieldKey === "portfolioUrl" ||
                    fieldKey === "mostEngagedPost" ||
                    fieldKey === "contentExamplePost";
                  const stripScheme = (v: string) =>
                    v.replace(/^https?:\/\//i, "");
                  const displayValue = isUrlField
                    ? stripScheme(profileValue)
                    : profileValue;
                  const displayPlaceholder =
                    isUrlField && option.placeholder
                      ? stripScheme(option.placeholder)
                      : option.placeholder;
                  return (
                    <StyledTextField
                      fullWidth
                      multiline={option.multiline}
                      minRows={option.multiline ? 3 : undefined}
                      placeholder={displayPlaceholder}
                      value={displayValue}
                      onChange={(e: CustomEvent) => {
                        const raw = e.target.value;
                        const valueToStore = isUrlField
                          ? stripScheme(raw).length > 0
                            ? `https://${stripScheme(raw)}`
                            : ""
                          : raw;
                        setProfile(
                          (prev) =>
                            ({
                              ...(prev ?? {}),
                              [fieldKey]: valueToStore,
                            }) as OrganizationProfile,
                        );
                      }}
                      InputProps={
                        isUrlField
                          ? {
                              startAdornment: (
                                <InputAdornment
                                  disablePointerEvents
                                  position="start"
                                >
                                  https://
                                </InputAdornment>
                              ),
                            }
                          : undefined
                      }
                      onBlur={(e: CustomEvent) => {
                        if (!isPortfolio) return;
                        const stripped = stripScheme(e.target.value).trim();
                        const url = stripped.length
                          ? `https://${stripped}`
                          : "";
                        if (url.length > 0 && !isValidHttpUrl(url)) {
                          setError({
                            field: "portfolioUrl",
                            errorMessage:
                              "Please enter a valid URL (https://www.example.com)",
                          });
                        } else {
                          setError({ field: "", errorMessage: "" });
                        }
                      }}
                      error={portfolioErrored}
                      helperText={portfolioErrored ? error.errorMessage : ""}
                    />
                  );
                })()
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
